import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

axios.defaults.withCredentials = true;

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('fixif_token'));
  const [currentBranch, setCurrentBranchState] = useState(localStorage.getItem('fixif_branch') || '');
  const [branches, setBranches] = useState([]);

  const getAuthHeaders = useCallback(() => {
    if (token) return { Authorization: `Bearer ${token}` };
    return {};
  }, [token]);

  useEffect(() => { checkAuth(); }, []);

  const checkAuth = async () => {
    const savedToken = localStorage.getItem('fixif_token');
    if (!savedToken) { setLoading(false); return; }
    try {
      const res = await axios.get(`${API}/api/auth/me`, { headers: { Authorization: `Bearer ${savedToken}` } });
      setUser(res.data);
      setToken(savedToken);
      fetchBranches(savedToken);
    } catch {
      localStorage.removeItem('fixif_token');
      setToken(null);
      setUser(null);
    }
    setLoading(false);
  };

  const fetchBranches = async (tkn) => {
    try {
      const res = await axios.get(`${API}/api/branches`, { headers: { Authorization: `Bearer ${tkn || token}` } });
      setBranches(res.data);
      // Auto-select first branch if none selected
      if (!localStorage.getItem('fixif_branch') && res.data.length > 0) {
        setCurrentBranchState(res.data[0].id);
        localStorage.setItem('fixif_branch', res.data[0].id);
      }
    } catch { /* ignore */ }
  };

  const setCurrentBranch = (branchId) => {
    setCurrentBranchState(branchId);
    localStorage.setItem('fixif_branch', branchId);
    // Refresh page to apply branch change everywhere
    window.location.reload();
  };

  const login = async (username, password) => {
    const res = await axios.post(`${API}/api/auth/login`, { username, password });
    const { token: newToken, user: userData } = res.data;
    localStorage.setItem('fixif_token', newToken);
    setToken(newToken);
    setUser(userData);
    fetchBranches(newToken);
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('fixif_token');
    localStorage.removeItem('fixif_branch');
    setToken(null);
    setUser(null);
    setCurrentBranchState('');
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, token, getAuthHeaders, currentBranch, setCurrentBranch, branches }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
